import { FunctionComponent, useMemo, useState } from "react";
import "./CSMShopfloorHeader.css";
import { useStoreActions, useStoreState } from "../../store";

import LOGO_SRC from "../../Icons/Logo.svg";
import INFO_SRC from "../../Icons/Info.svg";
import USER_SRC from "../../Icons/user.svg";
import SYSTEM_SRC from "../../Icons/gear.svg";
import IMAGE_SRC from "../../Icons/image.svg";

import { useLocation, useNavigate } from "react-router";
import {Tooltip} from "../../components/Tooltip";
import DOC_SRC from "../../Icons/documentIcon.svg"
import { isAnalyticsEnabled } from "../../utility/analyticsAccess";
import { useCan } from "../../access/useCan";
import { getAppVersionLabel } from "../../utility/appVersion";
import ScreenHelpButton from "../SupportAssistant/ScreenHelpButton";




let defaultOrganization: string | null = null;

type CSMShopfloorHeaderProps = {
  onLogoContainerClick?: () => void;
};

export function setDefaultOrganization(organization: string) {
  defaultOrganization = organization;
}

/**
 * The organization the session is currently connected to (what the header shows), or null
 * before it has been established. Read by the org switcher so it opens on the CURRENT org
 * rather than always on CORP — with the auto-tenant feature a CORP user can start the
 * session in another org, and an unchanged Submit must not silently move them.
 */
export function getDefaultOrganization(): string | null {
  return defaultOrganization;
}



const CSMShopfloorHeader: FunctionComponent<CSMShopfloorHeaderProps> = ({
  onLogoContainerClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Analytics tab is "active" (underlined) when the analytics page is showing —
  // the same active-tab treatment the Shop Floor button uses.
  const isAnalyticsActive = location.pathname === "/Analytics";

  // Analytics is an opt-in feature gated by the Key Vault secret AnalyticsEnabled.
  // Hide the tab entirely unless it's turned on (the route is guarded too).
  const analyticsEnabled = isAnalyticsEnabled();

  // Account dropdown (username click opens a menu instead of logging out directly)
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // --------- Store state needed for this header -----------
  const user = useStoreState((s) => s.user);
  const isManagerMode = useStoreState((s) => s.appSettings.isManagerMode);
  const setIsManagerMode = useStoreActions((a) => a.appSettings.setIsManagerMode);
  const setIsActiveJobMode = useStoreActions((a) => a.appSettings.setIsActiveJobMode);
  
const jobs = useStoreState((s) => s.appSettings.jobs);

const completedCount = useMemo(() => {
  if (!jobs || jobs.length === 0) return 0;
  return jobs.filter(j => j.operation === "COMPLETED").length;
}, [jobs]);


  if(defaultOrganization == null){
  defaultOrganization = user.info.whoto;
  }
  

  /**
   * What the header offers comes from the access map (`GET api/me`), never from the role bits
   * in the store, which the auth cache can hold for half an hour after they change. Every flag
   * is false while access is loading, after it failed and after a refused switch, so none of
   * these controls flashes in before the answer.
   *
   * - The Manager tab needs `manageSettings`, the Shop Floor tab `workJobs`. A manager holds
   *   `workJobs` too, so a Manager-only account now sees both tabs.
   * - Images and Documents need `viewJobs`. An Analytics-only user, whom the route guard keeps
   *   on Analytics, holds none of it, so they see neither: a control that visibly does nothing
   *   is worse than one that is not there.
   * - The Settings gear opens `/system`, so it needs what that route needs, `manageSettings`,
   *   as well as Manager view. A Shop Floor user whose stored view is still Manager gets no
   *   gear to a screen that would refuse them.
   * - The organisation switcher needs `switchOrganization`.
   *
   * ⚠️ HIDING IS COSMETIC. The route guards decide what opens, and the Function App decides
   * what is answered.
   */
  const canSeeManager = useCan("manageSettings");
  const canSeeShopfloor = useCan("workJobs");
  const canViewJobs = useCan("viewJobs");
  const canOpenSettings = useCan("manageSettings");
  const canSwitchOrganization = useCan("switchOrganization");

  // --------- Handlers ----------------------
  const handleLogoClick = () => {
    if (onLogoContainerClick) onLogoContainerClick();
    else navigate("/home");
  };

  const goDocuments = () => navigate("/documents");
  const goImage = () => navigate("/images");
  const goSystem = () => navigate("/system");
  const goAnalytics = () => navigate("/Analytics");
  const doLogout = () => navigate("/logout");

  const doChooseOrgClick = () => navigate("/choose-organization");

  // NEW: toggle + navigate (guarded by visibility)
  const handleManagerClick = () => {
    if (!canSeeManager) return;
    setIsManagerMode(true);
    setIsActiveJobMode(true);
    navigate("/home");
  };

  const handleShopFloorClick = () => {
    if (!canSeeShopfloor) return;
    setIsManagerMode(false);
    navigate("/home");
  };

  // The completed-count badge rides on the Manager tab, but only while Manager is
  // NOT the current mode — in Manager mode the count is shown by the
  // Active/Completed toggle on the page itself instead.
  const showCompletedBadge = !isManagerMode && completedCount > 0;

  // Swap wrappers based on mode (keep your existing classes). On the Analytics
  // page neither Shop Floor nor Manager is the active tab, so both drop their
  // underline — only the Analytics tab is underlined.
  const shopWrapperClass = !isManagerMode && !isAnalyticsActive
    ? "csm-shop-floor-wrapper"
    : "csm-manager-wrapper";

  const managerWrapperClass = isManagerMode && !isAnalyticsActive
    ? "csm-shop-floor-wrapper"
    : "csm-manager-wrapper";

  // Optionally: if the current mode isn't permitted by the user's roles,
  // you can auto-correct the mode here. Commented to avoid side-effects:
  // useEffect(() => {
  //   if (isManagerMode && !canSeeManager && canSeeShopfloor) setIsManagerMode(false);
  //   if (!isManagerMode && !canSeeShopfloor && canSeeManager) setIsManagerMode(true);
  // }, [isManagerMode, canSeeManager, canSeeShopfloor, setIsManagerMode]);

  
  return (
    <div className="csm-header">
      {/* LEFT: Logo + app names */}
      <div className="logo-parent">
        <div
          className="logo"
          onClick={handleLogoClick}
          role="button"
          aria-label="Home"
          tabIndex={0}
        >
          <img className="logo-child" src={LOGO_SRC} alt="" />
        </div>

        <div className="frame-parent">
          <div
            style={{
              color: "#36424A",   // Figma; was "black"
              fontSize: 20,
              fontFamily: "Noto Sans",
              fontWeight: "300",
              // "33px", not 33. React leaves lineHeight unitless, so `33` meant
              // 33 × the 20px font size = a 660px line box. It was invisible on
              // desktop only because .csm-header is a fixed 72px tall and never
              // clips, so the glyphs still centred where they should. The moment
              // the header is allowed to size to its content (which it is on a
              // phone, to wrap onto two rows) that line box makes it 660px tall.
              lineHeight: "33px",
              wordWrap: "break-word",
            }}
          >
            CSM
          </div>

          {/* Manager tab (manageSettings) */}
          {canSeeManager && (
            <>
            <div className={managerWrapperClass}>
              <div
  className="csm-manager"
  role="button"
  tabIndex={0}
  aria-pressed={isManagerMode}
  data-csm-step="header.manager-tab"
  onClick={handleManagerClick}
  onKeyDown={(e) => e.key === "Enter" && handleManagerClick()}
  title="Manager"
>
  Manager
  {showCompletedBadge && (
    <span className="sfh-csm-toggle__badge">
      {" "}{" "}{" "}{" "}{completedCount}
    </span>
  )}
</div>
            </div>
            {/* Holds the badge's width open while the badge is absent. That 24px
                is the ONLY thing in the header's top row that differs between
                Manager and Shop Floor mode, and on a phone the row has just a
                few pixels of slack — so the badge appearing was enough to push
                "Analytics" onto a second line, making the whole header (and every
                utility icon beneath it) jump 18px each time you switched modes.

                It sits OUTSIDE the tab wrapper on purpose. Inside, it padded the
                wrapper's border-bottom — the active-tab underline — so the
                underline ran ~24px past the word "Manager". The two never collide
                in the original design, because the badge only shows while Manager
                is the INACTIVE tab, whose underline is transparent.

                Zero-width on desktop (`display: none`), so nothing there moves. */}
            {!showCompletedBadge && (
              <span className="sfh-csm-toggle__badge-reserve" aria-hidden="true" />
            )}
            </>
          )}

          {/* Shop Floor tab (workJobs) */}
          {canSeeShopfloor && (
            <div className={shopWrapperClass}>
              <div
                className="csm-manager"
                role="button"
                tabIndex={0}
                aria-pressed={!isManagerMode}
                data-csm-step="header.shopfloor-tab"
                onClick={handleShopFloorClick}
                onKeyDown={(e) => e.key === "Enter" && handleShopFloorClick()}
                title="Shop Floor"
              >
                Shop Floor
              </div>
            </div>
          )}

          {/* Analytics button — same font + active-underline treatment as the tabs
              above, underlined while the analytics page is showing. Hidden unless
              the AnalyticsEnabled Key Vault flag is on. */}
          {analyticsEnabled && (
            <div className={isAnalyticsActive ? "csm-shop-floor-wrapper" : "csm-manager-wrapper"}>
              <div
                className="csm-manager"
                role="button"
                tabIndex={0}
                aria-pressed={isAnalyticsActive}
                data-csm-step="header.analytics-tab"
                onClick={goAnalytics}
                onKeyDown={(e) => e.key === "Enter" && goAnalytics()}
                title="Analytics"
              >
                Analytics
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: utilities only (dropdowns removed) */}
      <div className="button-link-parent">
        {/* Images and Documents are shop-floor screens (viewJobs). */}
        {canViewJobs && (
          <div
            className="utility-link"
            data-csm-step="header.images"
            onClick={goImage}
            role="button"
            title="Images"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goImage()}
          >
            <img className="user-icon" alt="" src={IMAGE_SRC} />
            <div className="select-warehouse"> Images</div>
          </div>
        )}
        {canViewJobs && (
          <div
            className="utility-link"
            data-csm-step="header.documents"
            onClick={goDocuments}
            role="button"
            title="Documents"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goDocuments()}
          >
            <img className="user-icon" alt="" src={DOC_SRC} />
            <div className="select-warehouse"> Documents</div>
          </div>
        )}
        {/* User name (click => account dropdown, not immediate logout) */}
        <div
          className="utility-link csm-user-menu"
          data-csm-step="header.account"
          role="button"
          title="Account"
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          tabIndex={0}
          onClick={() => setUserMenuOpen((o) => !o)}
          onKeyDown={(e) => e.key === "Enter" && setUserMenuOpen((o) => !o)}
          style={{ position: "relative" }}
        >
          <img className="user-icon" alt="" src={USER_SRC} />
          <div className="select-warehouse">
            {(user as any)?.info?.name ?? (user as any)?.fullName ?? "User"}
          </div>

          {userMenuOpen && (
            <>
              {/* click-away backdrop */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                }}
                style={{ position: "fixed", inset: 0, zIndex: 999 }}
              />
              <div
                role="menu"
                className="csm-user-menu__dropdown"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #c4c7c8",
                  borderRadius: 4,
                  minWidth: 160,
                  zIndex: 1000,
                }}
              >
                {/* caret pointing up at the user name: border triangle + white fill */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: -7,
                    right: 16,
                    width: 0,
                    height: 0,
                    borderLeft: "7px solid transparent",
                    borderRight: "7px solid transparent",
                    borderBottom: "7px solid #c4c7c8",
                  }}
                />
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: -6,
                    right: 17,
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderBottom: "6px solid #fff",
                  }}
                />
                <div
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => {
                    setUserMenuOpen(false);
                    doLogout();
                  }}
                  onKeyDown={(e) =>
                    e.key === "Enter" && (setUserMenuOpen(false), doLogout())
                  }
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    textAlign: "right",
                    fontWeight: 300,
                    fontFamily: "Noto Sans, sans-serif",
                  }}
                >
                  Log out
                </div>
              </div>
            </>
          )}
        </div>

        {canSwitchOrganization && (
  <div
    className="utility-link"
    data-csm-step="header.org-switcher"
    onClick={doChooseOrgClick}
    role="button"
    title="Change Organization"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && doChooseOrgClick()}
  >
    <div className="select-corporation">
      {defaultOrganization}
    </div>
  </div>
)}


        {/* Settings gear: in Manager view, for a user who may open /system (manageSettings).
            `isManagerMode` is a persisted VIEW, not a permission, so it is never enough on its
            own. */}
        {isManagerMode && canOpenSettings && (
          <div
            className="utility-link"
            data-csm-step="header.system-gear"
            onClick={goSystem}
            role="button"
            title="Settings"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goSystem()}
          >
            <img className="user-icon csm-gear-icon" alt="" src={SYSTEM_SRC} />
            <div className="select-warehouse"></div>
          </div>
        )}

        {/* The per-screen "?" — one press starts this screen's walkthrough. It used to float in
            the bottom-right corner over whatever the user was working on; it lives here now so
            help is in ONE place on every screen, beside the other utilities.

            It is self-gating (feature flag, the path→screen lookup, the user's access to that
            screen, and whether the screen has a tour), so it renders nothing of its own accord
            on a screen it has nothing to say about — this mount needs no condition of its own.

            ⚠️ ICON ONLY, NO LABEL, and that is the note above about the badge restated: the
            header's rows have only a few pixels of slack on a phone, and a word here would push
            a utility off the row exactly as the completed-count badge pushed "Analytics" off the
            branding row. It is announced by its aria-label instead. */}
        <ScreenHelpButton />

        {/* Info (about) */}
        <div className="info-wrapper" role="button" tabIndex={0} aria-label="About">
  

  {/* Version comes from the AppVersion Key Vault secret, not source. */}
  <Tooltip text={getAppVersionLabel()}>
    <img className="path-346-icon" alt="" src={INFO_SRC} />
  </Tooltip>
</div>
      </div>
    </div>
  );
};

export default CSMShopfloorHeader;